import React from 'react';
import NewProjectWizardCard from './NewProjectWizardCard';
import styles from './css/index.module.css';
import uploadArrow from './assets/upload-arrow.svg';
import fileDoc from './assets/file-doc.svg';
import { isValidExtensionToUpload } from '../../utils/commonUtils';
import { validExtensions } from '../../utils/misc';
import { formatFileSize } from './projectSetupApi';

const ACCEPT = validExtensions.map(ext => `.${ext}`).join(',');

const fileKey = file => `${file.name}-${file.size}-${file.lastModified}`;

const CreateProjectStep2 = ({ files = [], onChange, onContinue, onSkip, onBack, continueLoading }) => {
    const [isDragging, setIsDragging] = React.useState(false);
    const fileInputRef = React.useRef(null);

    const addFiles = incoming => {
        const next = [...files];
        const existing = new Set(files.map(fileKey));
        incoming.forEach(file => {
            const extension = file.name.includes('.') ? file.name.split('.').pop() : '';
            if (!extension || !isValidExtensionToUpload(extension)) return;
            const key = fileKey(file);
            if (existing.has(key)) return;
            existing.add(key);
            next.push(file);
        });
        onChange({ context_files: next });
    };

    const removeFile = target => {
        onChange({ context_files: files.filter(file => fileKey(file) !== fileKey(target)) });
    };

    const handleDivClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = e => {
        addFiles(Array.from(e.target.files || []));
        e.target.value = '';
    };

    const handleDragEnter = e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = e => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        addFiles(Array.from(e.dataTransfer.files || []));
    };

    const handleKeyDown = e => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleDivClick();
        }
    };

    return (
        <NewProjectWizardCard
            step={2}
            title="Add project context"
            subtitle="Add any documents that can help Lens understand the engagement. You can also do this later."
            onContinue={onContinue}
            onSkip={onSkip}
            onBack={onBack}
            continueDisabled={continueLoading}
            continueLoading={continueLoading}
        >
            <div
                className={`${styles.dropzone} ${isDragging ? styles.dropzoneDragging : ''}`}
                role="button"
                tabIndex={0}
                onClick={handleDivClick}
                onKeyDown={handleKeyDown}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <img src={uploadArrow} alt="" width={24} height={24} className={styles.uploadIcon} />
                <p className={styles.dropzoneTitle}>
                    Drag & drop files or <span className={styles.dropzoneTitleUnderline}>click to upload</span>
                </p>
                <p className={styles.dropzoneHint}>
                    Engagement letter · Scope document · IM / CIM · Teaser ·
                    <br />
                    Investment memo · Project brief · Other relevant documents
                </p>
                <input
                    multiple
                    type="file"
                    accept={ACCEPT}
                    ref={fileInputRef}
                    className={styles.hiddenFileInput}
                    onChange={handleFileChange}
                />
            </div>
            {files.length > 0 ? (
                <ul className={styles.fileList}>
                    {files.map(file => (
                        <li key={fileKey(file)} className={styles.fileListItem}>
                            <div className={styles.fileListMeta}>
                                <img src={fileDoc} alt="" width={14} height={14} className={styles.fileListIcon} />
                                <span className={styles.fileName}>{file.name}</span>
                                <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
                            </div>
                            <button type="button" className={styles.fileRemove} onClick={() => removeFile(file)}>
                                Remove
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </NewProjectWizardCard>
    );
};

export default CreateProjectStep2;
